import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/utils';
import { BookingDetails, BookingDetailsDrawerProps, DrawerTab, ExpandedSections } from '../types/booking-details.types';
import { DetailsTab } from './booking-details/DetailsTab';
import { DocumentsTab } from './booking-details/DocumentsTab';
import { DrawerFooter } from './booking-details/DrawerFooter';
import { DrawerHeader } from './booking-details/DrawerHeader';
import { TimelineTab } from './booking-details/TimelineTab';
import { LoadingSpinner } from './ui/LoadingSpinner';

// Re-export types for backward compatibility
export type { BookingDetails } from '../types/booking-details.types';

export function BookingDetailsDrawer({ booking, isOpen, onClose, onAction }: BookingDetailsDrawerProps) {
  const [internalBooking, setInternalBooking] = useState<BookingDetails | null>(booking);
  const [activeTab, setActiveTab] = useState<DrawerTab>('details');
  const [expandedSections, setExpandedSections] = useState<ExpandedSections>({
    participants: false,
    services: false,
    configuration: false,
    breakdown: false,
  });

  // ✅ FIX: Réinitialiser internalBooking à null pendant le chargement
  useEffect(() => {
    if (booking) {
      setInternalBooking(booking);
      setActiveTab('details');
    } else {
      // Réinitialiser immédiatement pour éviter d'afficher l'ancienne réservation
      setInternalBooking(null);
    }
  }, [booking]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
    } else {
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  const toggleSection = (section: keyof ExpandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const b = internalBooking;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black/60 backdrop-blur-sm transition-all duration-300',
          isOpen ? 'opacity-100 z-[9998]' : 'opacity-0 pointer-events-none z-[-1]',
        )}
        onClick={onClose}
        aria-hidden="true"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      />

      {/* Modal Container */}
      <div
        className={cn(
          'fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-all duration-300',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      >
        {/* Modal Content */}
        <div
          className={cn(
            'relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col transition-all duration-300',
            isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0',
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          onClick={(e) => e.stopPropagation()}
        >
          {b ? (
            <>
              <DrawerHeader booking={b} activeTab={activeTab} setActiveTab={setActiveTab} onClose={onClose} />

              <div className="flex-1 overflow-y-auto bg-slate-50/50 custom-scrollbar">
                {activeTab === 'details' && (
                  <DetailsTab booking={b} expandedSections={expandedSections} toggleSection={toggleSection} />
                )}
                {activeTab === 'timeline' && <TimelineTab booking={b} />}
                {activeTab === 'documents' && <DocumentsTab booking={b} />}
              </div>

              <DrawerFooter
                booking={b}
                onAction={(action, id) => {
                  if (action === 'pro-forma') {
                    window.location.href = `/quotes?search=${b.ref}`;
                    onClose();
                  } else onAction(action, id);
                }}
                onClose={onClose}
              />
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
              <LoadingSpinner message="Chargement des détails..." submessage="Veuillez patienter" size="md" />
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
